/* global pendo */
import { useNavigate } from "react-router-dom";
import {
  setUser,
  clearUser,
  clearToken,
  setToken,
  selectUser,
  selectToken,
  setCollections,
  setEditingCollection,
  selectEditingCollection,
} from "../redux/features/appData";
import { useDispatch, useSelector } from "react-redux";
import { AuthSource, loginUser } from "../services/AuthService";
import { apiGetUserByEmail, apiGetWorkspaceCollections } from "../api";
import { setAxiosAuthToken } from "../services/client";
import config from "environment";
import { useConnectedAppRefresh } from "./useConnectedAppRefresh";
import {
  clearExtensionLogoutFlag,
  getExtensionLogoutFlag,
} from "../utils/localStorage";
import { useAccount } from "./useAccount";
import { identifyUserWithCustomerIO } from "./useAnalytics";

const defaultRoute = config.defaultRoute;

export type AuthContextProviderValue = {
  login: (payload: LoginPayload) => void;
  logout: () => void;
  checkStatus: () => void;
};

export type LoginPayload = {
  email: string;
  passwordKey: string;
  authSource: AuthSource;
};

export type VerifyEmailPayload = {
  userId: string;
  email: string;
  token: string;
};

export enum AuthErrorCodes {
  AUTH_INCORRECT_PASSWORD = "AUTH_INCORRECT_PASSWORD",
  AUTH_NO_USER_FOUND = "AUTH_NO_USER_FOUND",
  AUTH_FAILED = "AUTH_FAILED",
  AUTH_SYSTEM_USER = "AUTH_SYSTEM_USER",
}

export type LoginResponse = {
  success: boolean;
  error?: string;
};

export const useAuth = () => {
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const editingCollection = useSelector(selectEditingCollection);

  const acct = useAccount();
  const refreshConnectedApps = useConnectedAppRefresh();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const login = async (
    payload: LoginPayload,
    onboardingState: string = "none"
  ): Promise<LoginResponse> => {
    //console.log("[useAuth/login] payload: ", payload);
    const newToken = await loginUser(payload);

    console.log("[useAuth/login] newToken: ", newToken);
    if (newToken === AuthErrorCodes.AUTH_SYSTEM_USER) {
      return { success: false, error: "Invalid account." };
    }

    if (newToken === AuthErrorCodes.AUTH_NO_USER_FOUND) {
      // toast("No user exists for that email", toastFail);
      return { success: false, error: "No user exists for that email" };
    }

    if (newToken === AuthErrorCodes.AUTH_INCORRECT_PASSWORD) {
      // toast("Incorrect Password", toastFail);
      return {
        success: false,
        error: "Incorrect password or Google authed account.",
      };
    }

    if (newToken === AuthErrorCodes.AUTH_FAILED) {
      // toast("Login Failed", toastFail);
      return { success: false, error: "Login Failed" };
    }

    if (newToken) {
      setAxiosAuthToken(newToken);
      const [loggedInUser] = await Promise.all([
        apiGetUserByEmail(payload.email),
      ]);

      dispatch(setUser(loggedInUser));
      identifyUserWithCustomerIO(loggedInUser);
      dispatch(setToken(newToken));

      // set collection correctly
      let theCollectionId = undefined;

      if (loggedInUser.currentWorkspaceId) {
        const workspaceCollections = await apiGetWorkspaceCollections(loggedInUser.currentWorkspaceId);
        if (workspaceCollections && workspaceCollections.length > 0) {
          theCollectionId = workspaceCollections[0].id;
          dispatch(setEditingCollection(theCollectionId));
        }
      }

      await acct.loadAccountInfo(loggedInUser.id);
      refreshConnectedApps.refresh();

      //trigger the content script for the extension to send the new store
      window.dispatchEvent(
        new Event("EXECUTE_WINSLOW_CONTENT_SCRIPT", {
          bubbles: true,
          composed: true,
        })
      );

      // if we are a new user, don't go to the stash yet. If we are just logging in, please do
      // NR** just messed with this
      const extensionLogoutFlag = getExtensionLogoutFlag();
      
      if (extensionLogoutFlag) {
        navigate("/extension/landing");
        clearExtensionLogoutFlag();
      } else {
        const stashUrl = localStorage.getItem("stashUrl");
        if (stashUrl) {
          // alert("stashUrl: " + stashUrl);
          console.error("[useAuth/login] stashUrl: " + stashUrl);
          localStorage.removeItem("stashUrl");
          navigate(stashUrl);
        } else {
          navigate("/waz/chat/" + theCollectionId);
        }
      }

      return { success: true };
    }

    // toast("Error Logging In", toastFail);
    return { success: false, error: "Error Logging In" };
  };

  const checkStatus = (logoutOnFail: boolean = true) => {
    if (!token || !user) {
      if (logoutOnFail) {
        logout();
        return;
      }
    }

    setAxiosAuthToken(token);
  };

  const logout = () => {
    dispatch(clearUser());
    dispatch(clearToken());
    setAxiosAuthToken(undefined);

    navigate("/logout", {
      replace: true,
    });
  };

  return {
    login,
    logout,
    checkStatus,
  };
};
